import React, { useEffect, useMemo, useRef, useState } from "react";

import { toast } from "react-toastify";
import {
	Bot,
	FlaskConical,
	Loader2,
	RefreshCw,
	Send,
	ShieldAlert,
	TriangleAlert,
	Wrench,
} from "lucide-react";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../components/ui/select";
import { cn } from "../../lib/utils";
import toastError from "../../errors/toastError";
import api from "../../services/api";

// O simulador: o administrador conversa com o agente como se fosse o cliente,
// sem WhatsApp e sem ticket. Cada resposta da IA mostra o que ela consultou,
// se rodou de verdade ou foi simulado, e quanto custou.
//
// Nada aqui entra em um atendimento de verdade: a ação interna é sempre
// simulada e a consulta de fora só roda quando o admin liga o interruptor.

const MOTIVOS = {
	limite: "a IA atingiu o limite de respostas",
	pedido: "o cliente pediu um atendente",
	erros: "a IA falhou duas vezes seguidas na ferramenta",
	voltas: "a IA não conseguiu concluir em 5 voltas",
	limite_de_chamadas: "a IA atingiu o limite de consultas",
	fluxo: "a conversa seria entregue ao fluxo",
};

const rotuloDoAgente = (agente) =>
	agente?.status === "draft" ? `${agente.name} (rascunho)` : agente?.name;

const horaDaMensagem = (at) => {
	if (!at) return "";

	const data = new Date(at);

	if (Number.isNaN(data.getTime())) return "";

	return data.toLocaleTimeString("pt-BR", {
		hour: "2-digit",
		minute: "2-digit",
	});
};

const textoDoValor = (valor) =>
	typeof valor === "string" ? valor : JSON.stringify(valor);

// O passo a passo de uma resposta: a ferramenta chamada, o que a IA mandou,
// o que voltou e se aquilo rodou de verdade ou foi simulado.
const PassosDaResposta = ({ passos = [] }) => {
	if (!passos.length) {
		return (
			<p className="text-xs text-muted-foreground">
				Nada foi consultado nesta resposta.
			</p>
		);
	}

	return (
		<ul className="space-y-2">
			{passos.map((passo, indice) => {
				const nome = passo?.ferramenta;

				// Passo sem ferramenta: ou é o provedor que respondeu, ou é o
				// motivo de a conversa ter acabado.
				if (!nome) {
					if (passo?.provider) {
						return (
							<li
								key={indice}
								className="rounded-lg border p-2.5 text-[11px] text-muted-foreground"
							>
								Provedor {passo.provider}
								{passo.model ? ` · modelo ${passo.model}` : ""}
							</li>
						);
					}

					if (passo?.motivo) {
						return (
							<li
								key={indice}
								className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-2.5 text-[11px] text-amber-700 dark:text-amber-400"
							>
								A conversa encerrou: {passo.motivo}
							</li>
						);
					}

					return (
						<li
							key={indice}
							className="rounded-lg border p-2.5 text-[11px] text-muted-foreground"
						>
							Resposta do modelo (volta {passo?.volta ?? 1}).
						</li>
					);
				}

				const argumentos = passo?.argumentos;
				const resultado = passo?.resultado;

				return (
					<li key={indice} className="rounded-lg border p-2.5">
						<div className="flex items-center justify-between gap-2">
							<span className="flex min-w-0 items-center gap-1.5 text-xs font-medium">
								<Wrench className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
								<span className="truncate">{nome}</span>
							</span>

							{typeof passo?.simulado === "boolean" && (
								<span
									className={cn(
										"shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium",
										passo.simulado
											? "bg-amber-500/10 text-amber-600 dark:text-amber-400"
											: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
									)}
								>
									{passo.simulado ? "simulado" : "real"}
								</span>
							)}
						</div>

						{argumentos && Object.keys(argumentos).length > 0 && (
							<ul className="mt-1.5 space-y-0.5 text-[11px] text-muted-foreground">
								{Object.entries(argumentos).map(([chave, valor]) => (
									<li key={chave}>
										<span className="font-medium text-foreground">{chave}:</span>{" "}
										{textoDoValor(valor)}
									</li>
								))}
							</ul>
						)}

						{resultado && (
							<pre className="mt-1.5 max-h-40 overflow-auto whitespace-pre-wrap break-words rounded-md bg-muted p-2 text-[11px] leading-relaxed">
								{resultado}
							</pre>
						)}

						{passo?.ok === false && (
							<p className="mt-1 text-[11px] text-destructive">
								A ferramenta falhou.
							</p>
						)}
					</li>
				);
			})}
		</ul>
	);
};

// Uma fala da conversa: cliente à direita, IA à esquerda e aviso do sistema no
// meio. As respostas da IA trazem o botão que abre o que ela consultou.
const MensagemDaConversa = ({
	mensagem,
	indice,
	passos = [],
	expandido,
	aoAlternar,
}) => {
	const { role, texto, at, custo, ms } = mensagem;

	if (role === "sistema") {
		return (
			<div className="flex justify-center py-1">
				<p className="max-w-md text-center text-[11px] leading-relaxed text-muted-foreground">
					{texto}
				</p>
			</div>
		);
	}

	const daCliente = role === "cliente";

	return (
		<div
			className={cn(
				"flex flex-col gap-1",
				daCliente ? "items-end" : "items-start"
			)}
		>
			<div
				className={cn(
					"max-w-[85%] whitespace-pre-wrap rounded-xl px-3 py-2 text-sm leading-relaxed",
					daCliente ? "bg-primary text-primary-foreground" : "bg-muted"
				)}
			>
				{texto}
			</div>

			<div
				className={cn(
					"flex items-center gap-2 text-[10px] text-muted-foreground",
					daCliente ? "flex-row-reverse" : ""
				)}
			>
				<span>{horaDaMensagem(at)}</span>

				{!daCliente && typeof ms === "number" && (
					<span>{(ms / 1000).toFixed(1)}s</span>
				)}

				{!daCliente && typeof custo === "number" && (
					<span>US$ {custo.toFixed(4)}</span>
				)}

				{!daCliente && passos.length > 0 && (
					<button
						type="button"
						className="underline underline-offset-2 hover:text-foreground"
						onClick={() => aoAlternar(indice)}
					>
						{expandido ? "Esconder o que a IA consultou" : "O que a IA consultou"}
					</button>
				)}
			</div>

			{/* Em tela estreita não há painel lateral: os passos abrem aqui,
			    embaixo da resposta que os produziu. */}
			{expandido && (
				<div className="w-full max-w-[85%] rounded-xl border p-2.5 lg:hidden">
					<PassosDaResposta passos={passos} />
				</div>
			)}
		</div>
	);
};

const AbaSimulador = ({ agentes = [] }) => {
	const [sessao, setSessao] = useState(null);
	const [agenteId, setAgenteId] = useState("");
	const [contatoNome, setContatoNome] = useState("Cliente de teste");
	const [contatoNumero, setContatoNumero] = useState("5500000000000");
	const [acoesReais, setAcoesReais] = useState(false);
	const [texto, setTexto] = useState("");
	const [iniciando, setIniciando] = useState(false);
	const [enviando, setEnviando] = useState(false);
	const [decidindo, setDecidindo] = useState(null);
	const [avisoModulo, setAvisoModulo] = useState(null);
	// Índice da resposta com os passos abertos: no painel lateral e, em tela
	// estreita, na própria resposta.
	const [passoAberto, setPassoAberto] = useState(null);

	const fimDaConversa = useRef(null);

	useEffect(() => {
		const alvo = fimDaConversa.current;
		if (alvo) alvo.scrollIntoView({ block: "end" });
	}, [sessao?.messages?.length, enviando]);

	// Sem agente escolhido, o primeiro da lista já vem na mão: escolher no
	// seletor é o que muda a partir daí.
	const agenteEscolhido =
		agenteId || (agentes.length ? String(agentes[0].id) : "");

	const mensagens = useMemo(() => sessao?.messages || [], [sessao]);

	const indiceDaUltimaResposta = useMemo(() => {
		for (let indice = mensagens.length - 1; indice >= 0; indice -= 1) {
			if (mensagens[indice]?.role === "ia") return indice;
		}

		return -1;
	}, [mensagens]);

	const indiceDoPainel =
		passoAberto !== null ? passoAberto : indiceDaUltimaResposta;

	const passosDoPainel =
		indiceDoPainel >= 0 ? mensagens[indiceDoPainel]?.passos || [] : [];

	const encerrada = sessao?.status === "encerrada";

	// O que mudou na tela depois da conversa começar só vale na próxima
	// sessão: o interruptor e o agente são gravados no começo dela.
	const precisaRecomecar = Boolean(
		sessao &&
			(sessao.agentId !== Number(agenteEscolhido) ||
				sessao.allowRealActions !== acoesReais)
	);

	const recarregarSessao = async () => {
		if (!sessao?.id) return;

		try {
			const { data } = await api.get(`/ai/simulations/${sessao.id}`);
			setSessao(data);
		} catch {
			setSessao(null);
		}
	};

	const tratarErro = async (err) => {
		const status = err?.response?.status;
		const mensagem =
			err?.response?.data?.error || err?.response?.data?.message || "";

		// Empresa sem o módulo de agentes de IA não chega nem na primeira
		// mensagem: a tela avisa em vez de tomar erro seco.
		if (status === 403 && mensagem.includes("ERR_MODULE_NOT_CONTRACTED")) {
			setAvisoModulo("Módulo Agentes de IA não contratado");
			return;
		}

		if (status === 410) {
			toast.error("A sessão venceu, comece outra.");
			setSessao(null);
			return;
		}

		// 409: a sessão já foi encerrada (ou a ação já foi decidida).
		// Recarregar mostra o estado certo — insistir seria brigar com o
		// servidor.
		if (status === 409) {
			toast.error("Esta conversa já foi encerrada. Comece outra.");
			await recarregarSessao();
			return;
		}

		toastError(err);
	};

	const criarSessao = async () => {
		const corpo = {
			agentId: Number(agenteEscolhido),
			contactName: contatoNome.trim() || undefined,
			contactNumber: contatoNumero.trim() || undefined,
		};

		// "Executar ações de verdade" só liga com true literal: string, 1 ou
		// qualquer coisa parecida não é permissão para agir no sistema real.
		if (acoesReais) corpo.allowRealActions = true;

		const { data } = await api.post("/ai/simulations", corpo);

		setSessao(data);
		setPassoAberto(null);
	};

	const comecar = async () => {
		if (!agenteEscolhido) {
			toast.error("Escolha o agente que vai responder.");
			return;
		}

		setIniciando(true);

		try {
			await criarSessao();
			toast.success("Conversa de teste iniciada.");
		} catch (err) {
			await tratarErro(err);
		}

		setIniciando(false);
	};

	const recomecar = async () => {
		setIniciando(true);

		try {
			// A sessão anterior é apagada: ela vale por 2 horas e ficaria
			// pendurada no banco sem ninguém conversando com ela.
			if (sessao?.id) {
				try {
					await api.delete(`/ai/simulations/${sessao.id}`);
				} catch {
					// Sessão vencida ou já apagada: o que importa é a nova.
				}
			}

			await criarSessao();
			toast.success("Conversa recomeçada.");
		} catch (err) {
			setSessao(null);
			await tratarErro(err);
		}

		setIniciando(false);
	};

	const enviar = async () => {
		const fala = texto.trim();

		if (!sessao?.id || !fala) return;

		setEnviando(true);

		try {
			const { data } = await api.post(`/ai/simulations/${sessao.id}/messages`, {
				text: fala,
			});

			setSessao(data.sessao);
			setTexto("");
		} catch (err) {
			await tratarErro(err);
		}

		setEnviando(false);
	};

	const decidir = async (decisao) => {
		if (!sessao?.id) return;

		setDecidindo(decisao);

		try {
			const { data } = await api.post(`/ai/simulations/${sessao.id}/decision`, {
				decisao,
			});

			setSessao(data.sessao);
			toast.success(decisao === "approve" ? "Ação aprovada." : "Ação recusada.");
		} catch (err) {
			await tratarErro(err);
		}

		setDecidindo(null);
	};

	const aoTeclar = (evento) => {
		if (evento.key !== "Enter" || evento.shiftKey) return;

		evento.preventDefault();
		enviar();
	};

	const alternarPassos = (indice) => {
		setPassoAberto((atual) => (atual === indice ? null : indice));
	};

	if (avisoModulo) {
		return (
			<div className="rounded-xl border bg-card py-16 text-center">
				<ShieldAlert className="mx-auto h-8 w-8 text-muted-foreground/50" />
				<p className="mt-3 text-sm font-medium">{avisoModulo}</p>
				<p className="mx-auto mt-1 max-w-sm text-xs leading-relaxed text-muted-foreground">
					O simulador faz parte do módulo de agentes de IA. Fale com quem
					administra a sua conta para liberar o módulo.
				</p>
			</div>
		);
	}

	return (
		<div className="space-y-3">
			{/* ---------------- cabeçalho ---------------- */}
			<div className="rounded-xl border bg-card p-4">
				<div className="grid gap-3 lg:grid-cols-[minmax(0,280px)_minmax(0,1fr)]">
					<div className="space-y-1.5">
						<Label>Agente</Label>
						<Select
							value={agenteEscolhido}
							onValueChange={setAgenteId}
							disabled={iniciando}
						>
							<SelectTrigger aria-label="Agente do simulador">
								<SelectValue placeholder="Escolha o agente" />
							</SelectTrigger>
							<SelectContent>
								{agentes.map((agente) => (
									<SelectItem key={agente.id} value={String(agente.id)}>
										{rotuloDoAgente(agente)}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<p className="text-xs text-muted-foreground">
							{agentes.length === 0
								? "Crie um agente na aba Agentes para testar aqui."
								: "Rascunho pode ser testado: é para isso que ele existe."}
						</p>
					</div>

					<div className="grid gap-3 sm:grid-cols-2">
						<div className="space-y-1.5">
							<Label htmlFor="simulador-nome">Nome do contato</Label>
							<Input
								id="simulador-nome"
								value={contatoNome}
								onChange={(e) => setContatoNome(e.target.value)}
								placeholder="Cliente de teste"
								disabled={iniciando}
							/>
						</div>
						<div className="space-y-1.5">
							<Label htmlFor="simulador-numero">Número do contato</Label>
							<Input
								id="simulador-numero"
								value={contatoNumero}
								onChange={(e) => setContatoNumero(e.target.value)}
								placeholder="5500000000000"
								disabled={iniciando}
							/>
							<p className="text-xs text-muted-foreground">
								A IA precisa ver um telefone. Ele não chega a lugar nenhum.
							</p>
						</div>
					</div>
				</div>

				{/* ---------------- ações de verdade ---------------- */}
				<div
					className={cn(
						"mt-3 rounded-lg border p-3 transition-colors",
						acoesReais
							? "border-destructive/40 bg-destructive/10"
							: "border-destructive/30 bg-destructive/5"
					)}
				>
					<div className="flex items-start justify-between gap-3">
						<div className="min-w-0">
							<Label
								htmlFor="simulador-acoes"
								className="flex items-center gap-1.5 text-destructive"
							>
								<TriangleAlert className="h-4 w-4" />
								Executar ações de verdade
							</Label>
							<p className="mt-1 text-xs leading-relaxed text-destructive">
								Ações como emitir segunda via ou abrir chamado vão acontecer no
								sistema real.
							</p>
						</div>
						<Switch
							id="simulador-acoes"
							checked={acoesReais}
							onCheckedChange={(v) => setAcoesReais(Boolean(v))}
						/>
					</div>
				</div>

				<div className="mt-3 flex flex-wrap items-center gap-2 border-t pt-3">
					{sessao ? (
						<Button
							variant="outline"
							className="h-9"
							disabled={iniciando || !agenteEscolhido}
							onClick={recomecar}
						>
							{iniciando ? (
								<Loader2 className="h-4 w-4 animate-spin" />
							) : (
								<RefreshCw className="h-4 w-4" />
							)}
							Recomeçar
						</Button>
					) : (
						<Button
							className="h-9"
							disabled={iniciando || !agenteEscolhido}
							onClick={comecar}
						>
							{iniciando ? (
								<Loader2 className="h-4 w-4 animate-spin" />
							) : (
								<FlaskConical className="h-4 w-4" />
							)}
							Começar
						</Button>
					)}

					{precisaRecomecar && (
						<p className="text-xs text-amber-600 dark:text-amber-400">
							O que você mudou só vale na próxima sessão: use Recomeçar.
						</p>
					)}
				</div>
			</div>

			{/* ---------------- conversa + painel ---------------- */}
			<div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_320px]">
				<section className="flex min-w-0 flex-col rounded-xl border bg-card">
					<header className="flex flex-wrap items-center gap-2 border-b px-4 py-3">
						<Bot className="h-4 w-4 text-muted-foreground" />
						<p className="text-sm font-medium">
							{sessao ? "Conversa de teste" : "Nenhuma conversa iniciada"}
						</p>

						{sessao && (
							<span className="text-xs text-muted-foreground">
								{sessao.contactName} · {sessao.contactNumber}
							</span>
						)}

						{sessao && (
							<span className="ml-auto text-xs tabular-nums text-muted-foreground">
								{sessao.aiReplies || 0} resposta(s) · {sessao.toolCalls || 0}{" "}
								chamada(s)
							</span>
						)}
					</header>

					{/* Pendência de aprovação do atendente: a IA pediu para
					    agir e alguém precisa dizer sim ou não. */}
					{sessao?.pending?.mode === "aprovacao_atendente" && (
						<div className="m-4 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3">
							<p className="text-sm leading-relaxed">
								🤖 A IA quer executar:{" "}
								<span className="font-medium">
									{sessao.pending.description || sessao.pending.toolName}
								</span>
							</p>

							{Object.keys(sessao.pending.params || {}).length > 0 && (
								<ul className="mt-1.5 space-y-0.5 text-xs text-muted-foreground">
									{Object.entries(sessao.pending.params).map(([chave, valor]) => (
										<li key={chave}>
											<span className="font-medium text-foreground">{chave}:</span>{" "}
											{textoDoValor(valor)}
										</li>
									))}
								</ul>
							)}

							<div className="mt-2.5 flex gap-2">
								<Button
									size="sm"
									className="h-8 text-xs"
									disabled={Boolean(decidindo)}
									onClick={() => decidir("approve")}
								>
									{decidindo === "approve" && (
										<Loader2 className="h-3.5 w-3.5 animate-spin" />
									)}
									Aprovar
								</Button>
								<Button
									size="sm"
									variant="outline"
									className="h-8 text-xs"
									disabled={Boolean(decidindo)}
									onClick={() => decidir("reject")}
								>
									{decidindo === "reject" && (
										<Loader2 className="h-3.5 w-3.5 animate-spin" />
									)}
									Recusar
								</Button>
							</div>
						</div>
					)}

					{/* Pendência de confirmação: quem responde é o "cliente",
					    e ele está na própria conversa. */}
					{sessao?.pending?.mode === "confirma_cliente" && (
						<p className="m-4 rounded-lg border border-amber-500/40 bg-amber-500/5 p-2.5 text-xs leading-relaxed text-amber-700 dark:text-amber-400">
							A IA está esperando o cliente confirmar: responda na conversa.
						</p>
					)}

					<div className="max-h-[55vh] min-h-[200px] space-y-3 overflow-y-auto p-4">
						{!sessao && (
							<p className="py-10 text-center text-xs leading-relaxed text-muted-foreground">
								Escolha o agente e toque em Começar para conversar com ele como
								se fosse o cliente. Nada aqui entra em um atendimento de verdade.
							</p>
						)}

						{mensagens.map((mensagem, indice) => (
							<MensagemDaConversa
								key={`${mensagem.at}-${indice}`}
								mensagem={mensagem}
								indice={indice}
								passos={mensagem.passos || []}
								expandido={passoAberto === indice}
								aoAlternar={alternarPassos}
							/>
						))}

						{enviando && (
							<div className="flex items-center gap-2 text-xs text-muted-foreground">
								<Loader2 className="h-3.5 w-3.5 animate-spin" />
								IA digitando…
							</div>
						)}

						<div ref={fimDaConversa} />
					</div>

					<div className="border-t p-3">
						{encerrada && (
							<p className="mb-2 rounded-md bg-muted px-3 py-2 text-xs leading-relaxed text-muted-foreground">
								A conversa terminou:{" "}
								{MOTIVOS[sessao.endedReason] || sessao.endedReason}. Use
								Recomeçar para começar outra.
							</p>
						)}

						<div className="flex items-end gap-2">
							<textarea
								rows={2}
								value={texto}
								disabled={!sessao || encerrada || enviando}
								onChange={(e) => setTexto(e.target.value)}
								onKeyDown={aoTeclar}
								placeholder={
									sessao
										? "Fale como o cliente..."
										: "Comece uma conversa para escrever"
								}
								aria-label="Fala do cliente"
								className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
							/>
							<Button
								className="h-10 shrink-0"
								disabled={!sessao || encerrada || enviando || !texto.trim()}
								onClick={enviar}
							>
								{enviando ? (
									<Loader2 className="h-4 w-4 animate-spin" />
								) : (
									<Send className="h-4 w-4" />
								)}
								Enviar
							</Button>
						</div>
					</div>
				</section>

				{/* Painel lateral: o que a IA consultou na resposta escolhida. */}
				<aside className="hidden lg:block">
					<div className="sticky top-4 rounded-xl border bg-card p-3">
						<header className="flex items-center justify-between gap-2">
							<p className="text-sm font-medium">O que a IA consultou</p>
							<Wrench className="h-4 w-4 text-muted-foreground" />
						</header>

						<div className="mt-2">
							<PassosDaResposta passos={passosDoPainel} />
						</div>
					</div>
				</aside>
			</div>
		</div>
	);
};

export default AbaSimulador;
